import { captureExceptionsToSentry } from '~/helpers/exceptionHandler';
import type { BufferEntry } from '../types';
import type createTemplates from './templates';

export function buildEmptyLineEntry(args: {
  actionPublicId: string;
  templates: Pick<ReturnType<typeof createTemplates>, 'emptyLine'>;
}): BufferEntry {
  const { actionPublicId, templates } = args;
  return {
    actionPublicId,
    line: templates.emptyLine(),
  };
}

export function buildErrorLineEntry(args: {
  actionPublicId: string;
  templates: Pick<ReturnType<typeof createTemplates>, 'errorLine'>;
}): BufferEntry {
  const { actionPublicId, templates } = args;
  return {
    actionPublicId,
    line: templates.errorLine(),
  };
}

/**
 * A wrapper that attempts to run a function that builds `BufferEntry` objects.
 * If the provided `fn` throws an exception, this wrapper will capture
 * the error in Sentry and will return an array containing a template line
 * with an error message.
 *
 * This is to prevent a single exception from crashing the entire changelog,
 * and instead handle errors on a per-line basis.
 *
 * @param options - An object containing the following properties:
 * @param options.actionPublicId - The public ID of the action.
 * @param options.templates - The templates
 * @param options.fn - The function that builds the `BufferEntry` objects.
 * @returns An array of `BufferEntry` objects, or an array containing an error line and an empty
 * line if an exception is thrown.
 */
export function tryBuildEntries(options: {
  actionPublicId: string;
  templates: Pick<ReturnType<typeof createTemplates>, 'emptyLine' | 'errorLine'>;
  fn: () => BufferEntry[];
}): BufferEntry[] {
  const { actionPublicId, templates, fn } = options;

  try {
    return fn();
  } catch (error) {
    captureExceptionsToSentry(error, { changelog: true });
    const emptyLineEntry = buildEmptyLineEntry({
      actionPublicId,
      templates,
    });
    const errorLineEntry = buildErrorLineEntry({
      actionPublicId,
      templates,
    });
    return [
      errorLineEntry,
      emptyLineEntry,
    ];
  }
}
